import sortBy from 'lodash/sortBy'
import {mapMaybes, maybe} from '@freckle/maybe'
import type {GenericAssignmentSessionAttrsT} from '@freckle/educator-entities/ts/common/models/generic-assignment-session'
import {getAssessmentAnswersStats} from '@freckle/educator-entities/ts/math/assessment/logic/assessments'
import type {AssessmentAnswerT} from '@freckle/educator-entities/ts/math/assessment/models/assessment-answer'
import type {MathQuestionMapT} from '@freckle/educator-entities/ts/math/common/models/questions'
import type {StudentAttrs} from '@freckle/educator-entities/ts/users/models/student'
import {formattedName, nameForSorting} from '@freckle/educator-entities/ts/users/models/student'
import {H2} from '@freckle/educator-materials/ts/components/typography'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'
import {table} from '../table.module.scss'

type Props = {
  assessmentSessions: Array<GenericAssignmentSessionAttrsT>
  assessmentAnswers: Array<AssessmentAnswerT>
  students: Array<StudentAttrs>
  questionMap: MathQuestionMapT
}

export default function AssessmentSummary(props: Props) {
  const {t} = useSafeTranslation()
  const {assessmentSessions, assessmentAnswers, students, questionMap} = props

  const sessionsData = getSessionsData(assessmentAnswers, assessmentSessions, questionMap, students)
  const rows = sortBy(sessionsData, [x => x.studentNameForSorting]).map(sessionData => {
    const scoreValue = maybe(
      () => t('COMMON_NON_APPLICABLE'),
      score => `${Math.round(score)}%`,
      sessionData.score
    )
    return (
      <tr key={sessionData.id}>
        <td data-pii>{sessionData.studentName}</td>
        <td>{sessionData.numQAnswered}</td>
        <td>{scoreValue}</td>
      </tr>
    )
  })

  return (
    <>
      <H2>{t('ASSESSMENT_REPORT_ASSESSMENT_SUMMARY_TITLE')}</H2>

      <table className={table}>
        <thead>
          <tr>
            <th>{t('ASSESSMENT_REPORT_ASSESSMENT_SUMMARY_STUDENT_COLUMN_TITLE')}</th>
            <th>{t('ASSESSMENT_REPORT_ASSESSMENT_SUMMARY_QUESTIONS_COLUMN_TITLE')}</th>
            <th>{t('ASSESSMENT_REPORT_ASSESSMENT_SUMMARY_ACCURACY_COLUMN_TITLE')}</th>
          </tr>
        </thead>
        <tbody>{rows}</tbody>
      </table>
    </>
  )
}

type SessionData = {
  id: number
  studentName: string
  studentNameForSorting: string
  numQAnswered: number
  score: number | undefined | null
}

// Get Assessments Session, collects data for test summary table
function getSessionsData(
  assessmentAnswers: Array<AssessmentAnswerT>,
  assessmentSessions: Array<GenericAssignmentSessionAttrsT>,
  questionMap: MathQuestionMapT,
  students: Array<StudentAttrs>
): Array<SessionData> {
  const studentByStudentId = new Map(students.map(student => [student.id, student]))

  return mapMaybes(assessmentSessions, ({sessionId, studentId}) => {
    const student = studentByStudentId.get(studentId)
    const studentAnswers = assessmentAnswers.filter(ans => ans.asmtSessId === sessionId)
    if (student !== undefined) {
      // Compute students stats on this list of assessment answers
      const {numQAnswered, accuracy} = getAssessmentAnswersStats(studentAnswers, questionMap)

      return {
        id: sessionId,
        studentName: formattedName(student),
        studentNameForSorting: nameForSorting(student),
        numQAnswered,
        score: accuracy
      }
    } else {
      return null
    }
  })
}
